import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChartBar, CheckCircle, XCircle, Clock, Warning, Buildings, User } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const fmtMoney = (n) =>
  typeof n === "number" ? n.toLocaleString(undefined, { style: "currency", currency: "USD" }) : "—";

const outcomeBadge = (o) => {
  const map = {
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    declined: "bg-red-50 text-red-700 border-red-200",
    withdrawn: "bg-zinc-100 text-zinc-700 border-zinc-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return `text-xs px-2 py-0.5 border font-mono uppercase tracking-wider ${map[o || "pending"]}`;
};

function Metric({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="border border-zinc-200 bg-white p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="gf-overline">{label}</div>
          <div className="font-display font-black text-3xl mt-2 tracking-tight tabular-nums">{value}</div>
        </div>
        <Icon size={22} className="text-zinc-400" />
      </div>
    </div>
  );
}

export default function Reporting() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], summary: {} });
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({ outcome: "approved", award_amount: "", outcome_notes: "" });
  const [saving, setSaving] = useState(false);
  const [scope, setScope] = useState("mine");
  const hasCompany = !!user?.company_id;

  const load = () => {
    api.get(`/reporting?scope=${scope}`).then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [scope]);

  const open_ = (p) => {
    setTarget(p);
    setForm({
      outcome: p.outcome || "pending",
      award_amount: p.award_amount ?? "",
      outcome_notes: p.outcome_notes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/proposals/${target.id}/outcome`, {
        outcome: form.outcome,
        award_amount: form.award_amount === "" ? null : Number(form.award_amount),
        outcome_notes: form.outcome_notes,
      });
      toast.success("Outcome reported");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const s = data.summary || {};

  return (
    <div className="max-w-7xl">
      <div className="gf-overline">Reporting</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">
        Outcomes &amp; impact.
      </h1>
      <p className="text-zinc-600 mt-3 max-w-2xl text-sm leading-relaxed">
        Track every submitted proposal. <b>Premium subscribers must report outcomes</b>{" "}
        within 14 days of submission to keep generating new proposals.
      </p>

      {hasCompany && (
        <div className="mt-6 inline-flex border border-zinc-200 bg-white" data-testid="reporting-scope">
          <button
            onClick={() => setScope("mine")}
            data-testid="r-scope-mine"
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 ${scope === "mine" ? "bg-blue-700 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
          >
            <User size={14} /> Mine
          </button>
          <button
            onClick={() => setScope("company")}
            data-testid="r-scope-company"
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 ${scope === "company" ? "bg-blue-700 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
          >
            <Buildings size={14} /> Company
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 mt-8">
        <Metric icon={ChartBar} label="Submitted" value={s.submitted ?? 0} testid="r-submitted" />
        <Metric icon={CheckCircle} label="Approved" value={s.approved ?? 0} testid="r-approved" />
        <Metric icon={XCircle} label="Declined" value={s.declined ?? 0} testid="r-declined" />
        <Metric icon={Clock} label="Pending" value={s.pending ?? 0} testid="r-pending" />
        <Metric icon={Warning} label="Overdue" value={s.overdue ?? 0} testid="r-overdue" />
      </div>

      <div className="mt-6 border border-zinc-200 bg-white p-6">
        <div className="gf-overline">Total awarded</div>
        <div className="font-display font-black text-4xl mt-2 tracking-tight tabular-nums" data-testid="r-total-award">
          {fmtMoney(s.total_award || 0)}
        </div>
      </div>

      <div className="mt-6 border border-zinc-200 bg-white">
        <div className="px-6 py-4 border-b border-zinc-200">
          <div className="gf-overline">Submitted proposals</div>
          <div className="font-display font-bold text-xl mt-1">Click a row to update outcome</div>
        </div>
        {(!data.items || data.items.length === 0) ? (
          <div className="p-12 text-center">
            <div className="gf-overline">No submitted proposals yet</div>
            <p className="text-zinc-600 mt-2 text-sm">
              Once you submit a proposal, it'll show up here for outcome tracking.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-zinc-200 text-left gf-overline">
                <th className="px-6 py-3">Title</th>
                <th className="px-6 py-3">Agency</th>
                {scope === "company" && <th className="px-6 py-3">Owner</th>}
                <th className="px-6 py-3">Submitted</th>
                <th className="px-6 py-3">Outcome</th>
                <th className="px-6 py-3">Award</th>
                <th className="px-6 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((p) => (
                <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`r-row-${p.id}`}>
                  <td className="px-6 py-4 font-semibold max-w-sm truncate">
                    <Link to={`/proposals/${p.id}`} className="hover:text-blue-700">{p.title}</Link>
                  </td>
                  <td className="px-6 py-4 text-zinc-600 font-mono text-xs">{p.grant_agency}</td>
                  {scope === "company" && (
                    <td className="px-6 py-4 text-zinc-600 text-xs">
                      <div className="font-semibold">{p.owner_name || "—"}</div>
                      <div className="font-mono text-zinc-500">{p.owner_email || ""}</div>
                    </td>
                  )}
                  <td className="px-6 py-4 text-zinc-500 font-mono text-xs">
                    {p.submitted_at ? new Date(p.submitted_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-6 py-4">
                    <span className={outcomeBadge(p.outcome)}>{p.outcome || "pending"}</span>
                  </td>
                  <td className="px-6 py-4 font-mono text-xs">{fmtMoney(p.award_amount)}</td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => open_(p)}
                      data-testid={`r-report-${p.id}`}
                      className="text-blue-700 font-semibold hover:underline uppercase tracking-wide text-xs"
                    >
                      Report →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="gf-sharp border-zinc-200" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-bold tracking-tight">Report outcome</DialogTitle>
            <DialogDescription>{target?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="gf-overline">Outcome</Label>
              <Select value={form.outcome} onValueChange={(v) => setForm((f) => ({ ...f, outcome: v }))}>
                <SelectTrigger data-testid="report-outcome-select" className="gf-sharp mt-2 border-zinc-300 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="gf-sharp">
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="declined">Declined</SelectItem>
                  <SelectItem value="withdrawn">Withdrawn</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.outcome === "approved" && (
              <div>
                <Label className="gf-overline">Award amount (USD)</Label>
                <Input
                  data-testid="report-award"
                  type="number"
                  min="0"
                  step="100"
                  value={form.award_amount}
                  onChange={(e) => setForm((f) => ({ ...f, award_amount: e.target.value }))}
                  className="gf-sharp mt-2 border-zinc-300 bg-white"
                  placeholder="e.g., 250000"
                />
              </div>
            )}
            <div>
              <Label className="gf-overline">Notes</Label>
              <Textarea
                data-testid="report-notes"
                value={form.outcome_notes}
                onChange={(e) => setForm((f) => ({ ...f, outcome_notes: e.target.value }))}
                className="gf-sharp mt-2 border-zinc-300 bg-white"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="gf-sharp border-zinc-300 uppercase font-bold tracking-wide">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="report-save" className="gf-sharp bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wide">
              {saving ? "Saving…" : "Save outcome"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
